import type { FC } from "react";
import { cx } from "../utils/cx";

export type IconChipColor = "brand" | "accent" | "gray" | "success" | "warning" | "error" | "blue";

const colors: Record<IconChipColor, string> = {
    brand: "bg-utility-brand-50 text-utility-brand-600 dark:bg-brand-500/15 dark:text-brand-300",
    accent: "bg-utility-green-50 text-utility-green-600 dark:bg-accent-500/15 dark:text-accent-300",
    gray: "bg-utility-neutral-50 text-utility-neutral-600",
    success: "bg-utility-green-50 text-utility-green-600",
    warning: "bg-utility-yellow-50 text-utility-yellow-600",
    error: "bg-utility-red-50 text-utility-red-600",
    blue: "bg-utility-blue-50 text-utility-blue-600",
};

const sizes = {
    sm: { box: "size-8 rounded-lg", icon: "size-4" },
    md: { box: "size-10 rounded-lg", icon: "size-5" },
    lg: { box: "size-12 rounded-xl", icon: "size-6" },
};

/**
 * Flat, tinted icon tile — one soft background, no rings. The kit's icon rule:
 * - bare coloured icon: toasts, callouts, inline rows, buttons;
 * - IconChip: stat/metric cards and list rows where the colour carries meaning;
 * - FeaturedIcon (ringed): only empty states and page heroes.
 */
export const IconChip = ({
    icon: Icon,
    color = "brand",
    size = "md",
    className,
}: {
    icon: FC<{ className?: string }>;
    color?: IconChipColor;
    size?: keyof typeof sizes;
    className?: string;
}) => (
    <span aria-hidden="true" className={cx("inline-flex shrink-0 items-center justify-center", sizes[size].box, colors[color], className)}>
        <Icon className={sizes[size].icon} />
    </span>
);
